import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, X, Upload, CheckCircle2, Clock, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbs = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export default function Returns() {
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ order_id: "", reason: "", images: [] });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get("/orders").then(r => setOrders(r.data.filter(o => o.dispatch_status !== "cancelled")));
    api.get("/returns").then(r => setReturns(r.data));
  };
  useEffect(() => { load(); }, []);

  const addImages = async (files) => {
    const slots = 5 - form.images.length;
    const picks = Array.from(files).slice(0, slots);
    if (!picks.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of picks) {
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${f.name} exceeds 5MB — skipped`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      if (uploaded.length) setForm(f => ({ ...f, images: [...f.images, ...uploaded] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.order_id) { toast.error("Select an order"); return; }
    if (!form.reason.trim()) { toast.error("Reason required"); return; }
    if (form.images.length < 1) { toast.error("At least 1 image is mandatory"); return; }
    setBusy(true);
    try {
      await api.post("/returns", form);
      toast.success("Return request submitted — awaiting admin approval");
      setDlg(false);
      setForm({ order_id: "", reason: "", images: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  const statusStyle = (s) => s === "approved" ? "text-green-700" : s === "rejected" ? "text-red-700" : "text-[#C9A968]";
  const renderStatusIcon = (s) => s === "approved" ? <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> : s === "rejected" ? <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />;

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="returns-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="label-eyebrow">Returns Portal</p>
          <h1 className="font-editorial text-5xl sm:text-6xl tracking-editorial mt-3">Return an order</h1>
        </div>
        <button onClick={() => setDlg(true)} className="btn-clay" data-testid="new-return-btn">
          <Upload className="w-4 h-4" strokeWidth={1.5} /> New Return Request
        </button>
      </div>

      <p className="text-[#5C5C5C] mb-10 max-w-2xl">Upload clear photos of the goods you wish to return (minimum 1, up to 5). Returns are subject to admin approval. Products marked &quot;Not Returnable&quot; cannot be returned.</p>

      {returns.length === 0 ? (
        <p className="text-[#8A8A8A]">You haven&apos;t requested any returns yet.</p>
      ) : (
        <div className="divide-y divide-[#1A1A1A]/10">
          {returns.map(r => (
            <div key={r.id} className="py-6 grid grid-cols-12 gap-4 items-center" data-testid={`return-${r.id}`}>
              <div className="col-span-4">
                <p className="label-eyebrow">Order {r.order_no}</p>
                <p className="text-sm mt-1">{r.reason.slice(0, 100)}{r.reason.length > 100 ? "…" : ""}</p>
              </div>
              <div className="col-span-3 flex gap-1">
                {r.images.slice(0,3).map((img, i) => (
                  <img key={`${r.id}-${img}-${i}`} src={toAbs(img)} alt="proof" className="w-16 h-16 object-cover bg-[#F0EFEA]" />
                ))}
                {r.images.length > 3 && <span className="text-xs text-[#8A8A8A] self-center ml-2">+{r.images.length - 3}</span>}
              </div>
              <div className="col-span-2 text-xs text-[#8A8A8A]">{new Date(r.created_at).toLocaleDateString()}</div>
              <div className={`col-span-3 text-right ${statusStyle(r.status)} inline-flex items-center gap-1 uppercase text-xs tracking-wider justify-end`}>
                {renderStatusIcon(r.status)} {r.status}
              </div>
            </div>
          ))}
        </div>
      )}

      {dlg && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="return-modal">
          <div className="bg-[#FAF9F6] max-w-lg w-full p-10 relative fade-up max-h-[90vh] overflow-y-auto">
            <button onClick={() => setDlg(false)} className="absolute top-4 right-4 text-[#5C5C5C]"><X className="w-5 h-5" strokeWidth={1.5} /></button>
            <p className="label-eyebrow">Request Return</p>
            <h2 className="font-editorial text-3xl tracking-editorial mt-3 mb-6">Details</h2>

            <label className="label-eyebrow block mb-1">Select Order</label>
            <select className="w-full h-10 border border-[#d6d3ce] rounded-none px-3 mb-5 text-sm bg-transparent" value={form.order_id} onChange={(e) => setForm(f => ({ ...f, order_id: e.target.value }))} data-testid="return-order-select">
              <option value="">Choose an order...</option>
              {orders.map(o => <option key={o.id} value={o.id}>{o.order_no} · ₹{o.total.toFixed(0)} · {new Date(o.created_at).toLocaleDateString()}</option>)}
            </select>

            <label className="label-eyebrow block mb-1">Reason for Return</label>
            <textarea rows={4} className="w-full bg-transparent border-b border-[#d6d3ce] py-3 outline-none focus:border-[#1E2C5A] text-sm resize-none mb-5" placeholder="Describe the issue..." value={form.reason} onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))} data-testid="return-reason" />

            <label className="label-eyebrow block mb-2">Product Images <span className="text-red-700">*</span> <span className="text-[#8A8A8A] normal-case tracking-normal">(min 1, up to 5 · max 5MB each)</span></label>
            <input type="file" accept="image/*" multiple disabled={uploading || form.images.length >= 5} onChange={(e) => { addImages(e.target.files); e.target.value = ""; }} className="block w-full text-sm mb-3 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#1E2C5A] file:text-white file:uppercase file:text-xs file:tracking-wider disabled:opacity-50" data-testid="return-images-input" />
            {uploading && <p className="text-xs text-[#C9A968] mb-3">Uploading…</p>}
            <div className="grid grid-cols-5 gap-2 mb-6">
              {form.images.map((img, i) => (
                <div key={img || `ret-img-${i}`} className="relative">
                  <img src={toAbs(img)} alt={`upload ${i}`} className="w-full aspect-square object-cover" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))} className="absolute top-0 right-0 bg-red-700 text-white w-5 h-5 flex items-center justify-center text-xs" data-testid={`remove-return-img-${i}`}>×</button>
                </div>
              ))}
            </div>

            <button onClick={submit} disabled={busy || uploading} className="btn-clay w-full" data-testid="return-submit">
              {busy ? "Submitting..." : "Submit Return Request"} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
